// Analytics Utility — dual-writes to GA4 and Supabase
// GA4:      real-time traffic, bounce rates, geo (handled automatically)
// Supabase: funnel events for week-over-week reporting via weekly_funnel view

import { supabase } from '../supabase';

// ============================================================================
// Module-level state — call initAnalytics() once on auth
// ============================================================================

let _userId = null;
let _userType = null; // 'anonymous' | 'registered'

// Browser QA uses ?e2e=1. Keep those interactions out of both reporting
// systems so repeated journey checks do not distort acquisition or PMF data.
const _isTestSession = (() => {
  try {
    return new URLSearchParams(window.location.search).get('e2e') === '1';
  } catch {
    return false;
  }
})();

// One session ID per browser tab, persisted across soft navigations
const _sessionId = (() => {
  const key = 'sonmi_session_id';
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = window.crypto?.randomUUID?.() || `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(key, id);
  return id;
})();

// First-touch ad attribution (Reddit community × message via UTMs).
// The utm_* params live in the URL only on the LANDING page — gone after the first
// soft navigation — so capture them once and persist to localStorage. First touch
// wins: we never overwrite, so the ad that ORIGINALLY brought the user survives the
// whole guest journey and past registration. Direct/organic visits store nothing,
// so a later ad click can still be captured as the first touch.
const _attribution = (() => {
  const KEY = 'sonmi_attribution';
  try {
    const stored = localStorage.getItem(KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* localStorage blocked — fall through to a fresh read */ }

  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  const utm = {};
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const v = p.get(k);
    if (v) utm[k] = v.slice(0, 200);
  }
  if (!Object.keys(utm).length) return {}; // nothing to attribute — don't persist an empty record

  utm.landing_referrer = (document.referrer || '').slice(0, 200);
  utm.first_seen = new Date().toISOString();
  try { localStorage.setItem(KEY, JSON.stringify(utm)); } catch { /* best effort */ }
  return utm;
})();

/** The captured first-touch ad cohort, or {} for a direct/organic visitor. */
export const getAttribution = () => _attribution;

/**
 * Call once from App.js after auth resolves.
 * Sets user context for all subsequent Supabase writes.
 */
export const initAnalytics = (userId, isAnonymous) => {
  _userId = userId || null;
  _userType = isAnonymous ? 'anonymous' : 'registered';
};


// ============================================================================
// Core writers
// ============================================================================

const writeToSupabase = (eventName, properties = {}) => {
  if (_isTestSession) return;
  if (!_userId) return; // Auth not ready yet — skip
  supabase
    .from('analytics_events')
    .insert({
      event_name: eventName,
      user_id:    _userId,
      user_type:  _userType,
      session_id: _sessionId,
      // Stamp the first-touch ad cohort onto EVERY event so the funnel can be split
      // by community × message. utm_* keys never collide with event props.
      properties: { ..._attribution, ...properties },
    })
    .then(({ error }) => {
      if (error) console.warn('Analytics write failed:', error.message);
    });
};

const writeToGA4 = (eventName, eventParams = {}) => {
  if (_isTestSession) return;
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

const track = (eventName, properties = {}) => {
  writeToGA4(eventName, properties);
  writeToSupabase(eventName, properties);
};


// ============================================================================
// User properties (GA4 only — no PII in Supabase event table)
// ============================================================================

export const setUserProperties = (properties) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', properties);
  }
};


// ============================================================================
// SESSION EVENTS
// ============================================================================

// Track once per session (App.js checks sessionStorage flag)
export const trackAppSessionStart = () => {
  track('app_session_start');
};

// Call on beforeunload with elapsed seconds
export const trackAppSessionEnd = (durationSeconds) => {
  track('app_session_end', { duration_seconds: durationSeconds });
};


// ============================================================================
// AUTHENTICATION EVENTS
// ============================================================================

// Climate flow, opened from the "The air is changing your skin" email.
//
// These also solve where an anonymous answer goes. track() already writes to
// analytics_events in Supabase, including for guests, so a climate picked
// without an account is readable server-side even though the profile copy
// stays in localStorage. No signed token needed.
export const trackClimateModalShown = () => {
  track('climate_modal_shown');
};

export const trackClimateSaved = ({ climate, waterType, anonymous }) => {
  track('climate_saved', {
    climate: Array.isArray(climate) ? climate.join('|') : String(climate || ''),
    water_type: waterType || 'unspecified',
    anonymous: Boolean(anonymous),
  });
};

export const trackClimateSignInClicked = () => {
  track('climate_sign_in_clicked');
};

export const trackClimateDismissed = ({ saved }) => {
  track('climate_dismissed', { saved: Boolean(saved) });
};

export const trackSignUp = (method = 'email') => {
  track('sign_up', { method });
};

export const trackLogin = (method = 'email') => {
  track('login', { method });
};

export const trackLogout = () => {
  track('logout');
};


// ============================================================================
// CONSENT EVENTS
// ============================================================================

export const trackConsentGiven = () => {
  track('consent_given');
};

export const trackConsentRevoked = () => {
  track('consent_revoked');
};


// ============================================================================
// PHOTO ANALYSIS EVENTS (separate so the funnel can distinguish them)
// ============================================================================

export const trackSkinPhotoAnalysis = () => {
  track('photo_analysis_skin');
};

export const trackProductPhotoAnalysis = () => {
  track('photo_analysis_product');
};

// Where the photo came from — the upload-vs-real-time split we couldn't answer
// before. Fired the moment a photo is actually chosen (not on analysis
// completion), so it's reliable even if the read later fails or the user drops
// off. source: 'guided' (live multi-angle scan) | 'camera' (single snap) |
// 'library' (device photo picker) | 'drop' (drag-and-drop). kind: skin | product.
export const trackPhotoUpload = ({ source, kind = 'skin' }) => {
  track('photo_upload', { source, kind });
};

// Fired when a user tries to send MORE THAN ONE product photo — either multi-
// selecting in the picker or adding a second while one is staged. Chat does one
// product at a time; this measures real demand for routing them to the existing
// Compare Products screen before we build that hand-off. via: 'multi_select' |
// 'add_second'. count = how many they were trying to send.
export const trackMultiProductAttempt = ({ count, via }) => {
  track('multi_product_attempt', { count, via });
};


// ============================================================================
// SKIN ANALYSIS EVENTS
// ============================================================================

export const trackSkinAnalysisStart = () => {
  track('skin_analysis_start');
};

// Volume only. We deliberately do NOT send skin_type or the specific concern to GA4
// (Google) — that's health-adjacent personal data and there is no consent to share it
// with a third party. concerns_count is a non-identifying count.
export const trackSkinAnalysisComplete = (analysisData) => {
  track('skin_analysis_complete', {
    concerns_count: analysisData.concerns?.length || 0,
  });
};


// ============================================================================
// CHAT EVENTS
// ============================================================================

export const trackChatMessageSent = (hasImage = false) => {
  track('chat_message_sent', { has_image: hasImage });
};

export const trackChatRecommendation = (recommendationType) => {
  track('chat_recommendation_received', { type: recommendationType });
};


// ============================================================================
// PRODUCT EVENTS
// ============================================================================

export const trackProductSaved = (productData) => {
  track('product_saved', {
    product_name: productData.name,
    brand:        productData.brand,
    category:     productData.category,
    list_name:    productData.listName || 'wishlist',
  });
};

export const trackProductAnalyzed = (productName) => {
  track('product_analyzed', { product_name: productName });
};

export const trackProductComparison = (productCount) => {
  track('product_comparison_start', { product_count: productCount });
};

export const trackProductComparisonComplete = (winnerProduct) => {
  track('product_comparison_complete', { winner: winnerProduct });
};

export const trackProductDeleted = (productName, listName) => {
  track('product_deleted', { product_name: productName, list_name: listName });
};


// ============================================================================
// NAVIGATION EVENTS
// ============================================================================

export const trackMenuOpened = () => {
  track('menu_opened');
};

export const trackProductCatalogViewed = () => {
  track('product_catalog_viewed');
};

export const trackProfileViewed = () => {
  track('profile_viewed');
};


// ============================================================================
// ERROR EVENTS
// ============================================================================

export const trackError = (errorType, errorCategory = 'unknown') => {
  const safeToken = (value, fallback) => {
    const token = String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9_.-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80);
    return token || fallback;
  };
  const category = safeToken(errorCategory, 'unknown');
  const allowedCategories = new Set([
    'timeout', 'auth', 'rate_limited', 'server', 'request',
    'network', 'cancelled', 'parse', 'unknown',
  ]);
  track('error_occurred', {
    error_type: safeToken(errorType, 'unknown_error'),
    error_category: allowedCategories.has(category) ? category : 'unknown',
  });
};

export const trackApiTimeout = (endpoint) => {
  track('api_timeout', { endpoint });
};


// ============================================================================
// ENGAGEMENT EVENTS
// ============================================================================

export const trackFeatureUsed = (featureName) => {
  track('feature_used', { feature_name: featureName });
};

export const trackSessionDuration = (durationSeconds) => {
  track('session_duration', { duration: durationSeconds });
};


// ============================================================================
// ONBOARDING / READ-FEEDBACK / REGISTRATION-PROMPT EVENTS
// (2026-07 onboarding revamp — the funnel Sofia asked to be able to answer:
//  did they scan · did they agree with the read · did they correct it and what ·
//  did they ask · did they click shade matching · did they see/dismiss the
//  register prompt. "Did they return another day" is derived from
//  app_session_start by distinct day per user_id.)
// ============================================================================

// Goal picker — which intents were chosen. Also how we size demand for
// features we don't build yet (e.g. colour analysis) without promising them.
export const trackIntentSelected = (intents = []) => {
  track('intent_selected', { intents, count: intents.length });
};

export const trackIntentSkipped = () => {
  track('intent_skipped');
};

// Outcome-led onboarding experiment. These events measure the behavioural
// funnel the redesign is meant to reveal: saw choices → chose an outcome →
// started a concrete journey → received its first result.
export const trackOutcomeOnboardingViewed = () => {
  track('outcome_onboarding_viewed');
};

export const trackOutcomeIntroViewed = () => {
  track('outcome_intro_viewed');
};

export const trackOutcomeIntroContinued = () => {
  track('outcome_intro_continued');
};

export const trackOutcomeIntroSignIn = () => {
  track('outcome_intro_sign_in');
};

export const trackOutcomeSelected = (outcome) => {
  track('outcome_selected', { outcome });
};

export const trackOutcomeJourneyStarted = (journey) => {
  track('outcome_journey_started', { journey });
};

export const trackOutcomeResultDelivered = (journey) => {
  track('outcome_result_delivered', { journey });
};

export const trackOutcomeJourneyFailed = (journey, reason = 'unknown') => {
  track('outcome_journey_failed', { journey, reason });
};

export const trackJourneyNextActionSelected = (action, journey = '') => {
  track('journey_next_action_selected', { action, journey });
};

export const trackRecentAnalysisOpened = () => {
  track('recent_analysis_opened');
};

export const trackComparisonIdentificationComplete = ({ total = 0, succeeded = 0, failed = 0 }) => {
  track('comparison_identification_complete', { total, succeeded, failed });
};

export const trackConsentDeclined = () => {
  track('consent_declined');
};


export const trackShadeRangeLookup = ({ status, productCount = 0, candidateCount = 0 }) => {
  track('shade_range_lookup', { status, product_count: productCount, candidate_count: candidateCount });
};

export const trackShadeRangeSelected = ({ brand, productType }) => {
  track('shade_range_selected', { brand, product_type: productType || '' });
};

export const trackProductAnalysisBasisSelected = (basis) => {
  track('product_analysis_basis_selected', { basis });
};

// "Does this sound like your skin?" — accuracy, not satisfaction.
export const trackReadFeedback = (verdict) => {
  track('read_feedback', { verdict }); // 'spot_on' | 'not_quite'
};

// Public Colour Analysis study: record the model's discrete comparison choices
// for every run, including runs where the participant does not leave feedback.
// Do not store the image, observations, evidence prose, or full report.
export const submitColourAnalysisRun = async ({ runId, analysis, suitability, colourExperience, photoCount = 1 }) => {
  if (_isTestSession) return { error: null };
  if (!_userId) return { error: new Error('Session is not ready') };
  const axes = ['temperature', 'overall_depth', 'colour_intensity', 'contrast'];
  const comparisons = {};
  for (const axis of axes) {
    const probeIds = analysis?.drape_comparisons?.[axis]?.preferred_probe_ids;
    const dimension = analysis?.dimensions?.[axis];
    if (!Array.isArray(probeIds) || probeIds.length !== 3 || !dimension) continue;
    comparisons[axis] = {
      preferred_probe_ids: probeIds.map((id) => typeof id === 'string' ? id.slice(0, 1) : null),
      score: dimension.score, label: dimension.label, confidence: dimension.confidence,
    };
  }
  const properties = {
    study: 'colour_public_v1', run_id: runId,
    analysis_version: analysis?.analysis_version || 'unknown',
    suitability_level: suitability?.level || 'unknown',
    photo_count: photoCount,
    suitability_confidence: suitability?.confidence ?? null,
    season: analysis?.seasonal_interpretation?.closest || null,
    neighbour: analysis?.seasonal_interpretation?.neighbour || null,
    comparisons,
    known_good_colours: String(colourExperience?.knownGood || '').trim().slice(0, 160),
    known_bad_colours: String(colourExperience?.knownBad || '').trim().slice(0, 160),
    favourite_colours: String(colourExperience?.favourites || '').trim().slice(0, 160),
  };
  const { error } = await supabase.from('analytics_events').insert({
    event_name: 'colour_analysis_run', user_id: _userId,
    user_type: _userType, session_id: _sessionId, properties,
  });
  return { error };
};

// The participant's judgment, optional note and profile labels are recorded.
// Photos and the full analysis are not included.
export const submitColourAnalysisFeedback = async ({ verdict, concern, note, season, neighbour, analysisVersion, runId }) => {
  if (_isTestSession) return { error: null };
  if (!_userId) return { error: new Error('Session is not ready') };
  const properties = {
    ..._attribution,
    study: 'colour_public_v1', verdict, concern: concern || 'none',
    season, neighbour, analysis_version: analysisVersion,
    ...(runId ? { run_id: runId } : {}),
  };
  const cleanNote = typeof note === 'string' ? note.trim().slice(0, 500) : '';
  if (cleanNote) properties.note = cleanNote;
  const { error } = await supabase.from('analytics_events').insert({
    event_name: 'colour_analysis_feedback', user_id: _userId,
    user_type: _userType, session_id: _sessionId, properties,
  });
  if (!error) {
    const aggregateProperties = { ...properties };
    delete aggregateProperties.note;
    writeToGA4('colour_analysis_feedback', aggregateProperties);
  }
  return { error };
};

// Which field the user says is wrong — the per-field accuracy signal for the
// vision model (tone wrong N% vs skin type wrong N%).
export const trackCorrectionSubmitted = (field, hasNote = false) => {
  track('correction_submitted', { field, has_note: hasNote });
};

export const trackShadeMatchesRequested = (source = 'cta') => {
  track('shade_matches_requested', { source }); // 'cta' | 'typed'
};

export const trackConversationContinuation = (source = 'suggested_question') => {
  track('conversation_continuation_selected', { source });
};

export const trackRegisterPromptShown = (trigger) => {
  track('register_prompt_shown', { trigger }); // 'post_analysis' | 'correction' | 'mandatory'
};

export const trackRegisterPromptDismissed = (trigger) => {
  track('register_prompt_dismissed', { trigger });
};

export const trackRegistrationAction = (action, source = 'account_modal') => {
  track('registration_action', { action, source });
};

export const trackAuthenticationFailed = (mode, code = 'unknown') => {
  const safeCode = String(code || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '_')
    .slice(0, 80);
  track('authentication_failed', { mode, code: safeCode || 'unknown' });
};

// Experiment B — the single highest-information click in the product: why the
// people we're losing chose not to register.
export const trackRegisterDismissReason = (reason) => {
  track('register_dismiss_reason', { reason });
};
